import { IUpdaterDriverStatusDeltaMessage } from '@numeo/types'
import type { AppDispatch, RootState } from 'store'
import { driverStatusDeltaChanges, findSchedulerDriver, patchDriverStatusDelta } from 'store/driverStatusMutation'
import { refreshDriverStatusRows } from 'store/schedulerSlice'

const pendingRefreshes = new WeakMap<AppDispatch, { projectName: string; nonce: number; cleanup: () => void }>()

const scheduleDriverRowsRefresh = (projectName: string, dispatch: AppDispatch, getState: () => RootState): void => {
    const nonce = getState().scheduler.dataRefreshNonce
    const pending = pendingRefreshes.get(dispatch)
    if (pending?.projectName === projectName && pending.nonce === nonce) return
    pending?.cleanup()
    // Patch chips immediately; coalesce active-tab reconciliation in one fixed window.
    // Hidden/blurred tabs retain a dirty listener until they become active.
    let timer: ReturnType<typeof setTimeout> | undefined
    const refresh = () => {
        if (document.visibilityState !== 'visible' || !document.hasFocus()) return
        cleanup()
        const state = getState()
        if (state.auth.application?.projectName === projectName && state.scheduler.dataRefreshNonce === nonce) dispatch(refreshDriverStatusRows())
    }
    const cleanup = () => {
        clearTimeout(timer)
        window.removeEventListener('focus', refresh)
        document.removeEventListener('visibilitychange', refresh)
        pendingRefreshes.delete(dispatch)
    }
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    pendingRefreshes.set(dispatch, { projectName, nonce, cleanup })
    if (document.visibilityState === 'visible' && document.hasFocus())
        timer = setTimeout(() => {
            timer = undefined
            refresh()
        }, 1000)
}

export const handleDriverStatusDelta = (data: IUpdaterDriverStatusDeltaMessage, dispatch: AppDispatch, getState: () => RootState): void => {
    // The board's own rows hold the history this delta changes; a driver on no loaded row has nothing to compare.
    const previous = findSchedulerDriver(getState().scheduler.processedDrivers, data.driverId)?.driverStatus
    const changed = driverStatusDeltaChanges(previous, data)
    dispatch(patchDriverStatusDelta({ driverId: data.driverId, upserts: data.upserts, deletedEntryIds: data.deletedEntryIds }))
    const projectName = getState().auth?.application?.projectName
    if (changed && projectName) scheduleDriverRowsRefresh(projectName, dispatch, getState)
}
